/**
 * @file HintPopover.tsx
 * @brief The "Tips" pill by a heading and its coach mark: web twin of the
 *        phone's VZHintPopover.
 *
 * @details
 * Same look and rules as FRONTEND `VZUIKit/VZHintPopover.tsx`:
 * - The pill (DES 16-10-2026, option A): a bulb and the word "Tips", the same
 *   soft fill and height as the action pills beside it; its outline shows, in
 *   brand, only while the tips are open. Was the "hold ring" icon, which test
 *   users found hard to read.
 * - The coach mark (DES 16-10-2026): the page dims, the pill shows again in a
 *   brand ring over the dim, and a card points at it with a small "Tips"
 *   heading, one row per tip (its gesture as an icon, the action in bold, the
 *   result in softer text) and "Got it". Was an outline bubble in the page
 *   under the heading, which read as part of the content.
 * Ported to web and the Mac app 20-10-2026 (DES). Click the dim, "Got it" or
 * Esc to close. The bubble still opens by itself until the person closes it
 * once (useHintPopover), then only from the pill.
 *
 * The tips themselves are web-true: the web has no press-and-hold, so each
 * screen lists what a mouse actually does there (right-click, click a row,
 * select words…), not the phone's wording.
 *
 * @references
 * - FRONTEND/src/shared/components/VZUIKit/VZHintPopover.tsx
 * - useHintPopover.ts (open on first visit, remembered once closed)
 *
 * @author DES
 * @date 05-10-2026
 * @updated 20-10-2026 — Tips pill + coach mark, as the phone
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { Hand, Lightbulb, MousePointerClick } from "lucide-react";
import { cn } from "@/shared/theme/cn";

/** The pill's face: bulb + "Tips". Also drawn over the dim by the coach mark. */
function TipsPillFace() {
  return (
    <>
      <Lightbulb className="size-[19px] text-brand" strokeWidth={2} aria-hidden />
      <span className="text-[15px] font-semibold text-app-text">Tips</span>
    </>
  );
}

/** The pill's box: the phone's tipsPill (36 high, 12 sides, fully round). */
const PILL =
  "inline-flex h-9 shrink-0 items-center gap-[5px] rounded-full border px-3 bg-[#F4F4F2] dark:bg-muted";

export function HintIcon({
  open,
  onClick,
  anchorRef,
  className,
}: {
  open: boolean;
  onClick: () => void;
  anchorRef?: RefObject<HTMLButtonElement | null>;
  className?: string;
}) {
  return (
    <button
      ref={anchorRef}
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-label={open ? "Hide tips" : "Show tips for this page"}
      className={cn(
        PILL,
        "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
        open ? "border-brand" : "border-transparent hover:bg-black/[0.06] dark:hover:bg-white/[0.12]",
        className,
      )}
    >
      <TipsPillFace />
    </button>
  );
}

const SIDE = 16;
const GAP = 14;
const CARET = 14;
const CARD_MAX_W = 400;

/** "Click a row" | " to see where it was said.": the action, then what it does. */
function splitTip(line: string): { lead: string; rest: string } {
  const m = line.match(/^(.+?)( (?:to|for) .*)$/);
  return m ? { lead: m[1], rest: m[2] } : { lead: line, rest: "" };
}

type Rect = { x: number; y: number; w: number; h: number };

export function HintBubble({
  lines,
  onClose,
  anchorRef,
}: {
  /** One tip per line. */
  lines: string[];
  onClose: () => void;
  anchorRef: RefObject<HTMLButtonElement | null>;
  /** Kept for the callers; the coach mark floats over the page. */
  className?: string;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<Rect | null>(null);
  const [view, setView] = useState({ w: 0, h: 0 });
  const [cardH, setCardH] = useState(0);

  const measure = useCallback(() => {
    const a = anchorRef.current?.getBoundingClientRect();
    setView({ w: window.innerWidth, h: window.innerHeight });
    if (a && a.width > 0) setAnchor({ x: a.left, y: a.top, w: a.width, h: a.height });
    const c = cardRef.current?.getBoundingClientRect();
    if (c) setCardH(Math.round(c.height));
  }, [anchorRef]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // The pill can still be settling when the page opens; it also moves with a
  // resize or scroll.
  useEffect(() => {
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const timers = [60, 300, 800].map((t) => window.setTimeout(measure, t));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("keydown", onKey);
      timers.forEach(clearTimeout);
    };
  }, [measure, onClose]);

  if (typeof document === "undefined") return null;

  const cardW = Math.min(CARD_MAX_W, Math.max(0, view.w - SIDE * 2));
  const centreX = anchor ? anchor.x + anchor.w / 2 : view.w - SIDE - 24;
  // The card's right edge under the pill's, kept inside the window.
  const left = Math.min(view.w - SIDE - cardW, Math.max(SIDE, centreX + 40 - cardW));
  const below = anchor ? anchor.y + anchor.h + GAP : 80;
  const fitsBelow = below + cardH <= view.h - 24;
  const top = fitsBelow ? below : (anchor ? anchor.y : view.h / 2) - GAP - cardH;
  const caretX = Math.min(cardW - 28, Math.max(20, centreX - left - CARET / 2));

  return createPortal(
    <div className="fixed inset-0 z-[90]" role="dialog" aria-modal="true" aria-label="Tips">
      <button
        type="button"
        aria-label="Close tips"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-black/[0.42] animate-in fade-in-0 dark:bg-black/[0.62]"
      />
      {anchor ? (
        <div
          aria-hidden
          className={cn(PILL, "pointer-events-none absolute border-brand bg-app-page dark:bg-app-page")}
          style={{ left: anchor.x, top: anchor.y, width: anchor.w, height: anchor.h }}
        >
          <TipsPillFace />
        </div>
      ) : null}
      <div
        ref={cardRef}
        className="absolute rounded-[18px] bg-app-card px-[18px] pb-2 pt-4 shadow-[0_12px_40px_rgba(0,0,0,0.18)] transition-opacity duration-150"
        style={{ top, left, width: cardW, opacity: cardH > 0 && anchor ? 1 : 0 }}
      >
        <span
          aria-hidden
          className="absolute rotate-45 bg-app-card"
          style={{
            left: caretX,
            width: CARET,
            height: CARET,
            ...(fitsBelow ? { top: -CARET / 2 } : { bottom: -CARET / 2 }),
          }}
        />
        {/* A small Tips heading, then one row per tip: its gesture as an
            icon, the action in bold, the result in softer text. */}
        <div className="mb-2.5 flex items-center gap-1.5">
          <Lightbulb className="size-[15px] text-brand" strokeWidth={2} aria-hidden />
          <span className="text-[13px] font-semibold tracking-[0.3px] text-app-text-secondary">Tips</span>
        </div>
        <ul className="flex flex-col gap-4">
          {lines.map((line) => {
            const { lead, rest } = splitTip(line);
            // A click on the web where the phone presses and holds.
            const Gesture = /^(click|double|right|tap|select|hover)/i.test(line) ? MousePointerClick : Hand;
            return (
              <li key={line} className="flex items-start gap-2.5">
                <span className="flex size-[30px] shrink-0 items-center justify-center rounded-full bg-app-page">
                  <Gesture className="size-4 text-brand" strokeWidth={2} aria-hidden />
                </span>
                <span className="flex-1 pt-1 text-[15px] leading-[21px] text-app-text-secondary">
                  <span className="font-semibold text-app-text">{lead}</span>
                  {rest}
                </span>
              </li>
            );
          })}
        </ul>
        <div className="mt-1 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            aria-label="Got it, close tips"
            className="rounded-full px-2 py-2 text-[15px] font-semibold text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
            autoFocus
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
